// ExportModal.tsx
import React, { useState, useEffect } from "react"
import { Modal, Input, Checkbox, Select } from "antd"
import DataQButton from "../Buttons/DataQButton.component"
import { notify } from "../Notifications/notify"
import { SelectOption, UI_INPUT_TYPES } from "types/ui.types"

// Define types for input configurations
interface AdditionalInput {
  name: string
  type: UI_INPUT_TYPES
  label?: string
  placeholder?: string
  checkboxLabel?: string
  required?: boolean
  multiple?: boolean
  defaultValue?: string | number | boolean | string[] | number[]
  options?: SelectOption[]
  disabled?: boolean | ((values: Record<string, unknown>) => boolean)
}

// Define the props interface with flexible confirm function
interface ExportModalProps {
  title: string
  isVisible: boolean
  cancel: (value: boolean) => void
  confirm: (
    filename: string,
    additionalValues: Record<string, unknown>
  ) => void | Promise<void>
  ExportButtonText?: string
  ExportButtonType?:
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "default"
    | "dashed"
    | "link"
    | "text"
    | "form"
  message: string
  additionalInputs?: AdditionalInput[]
  noFilename?: boolean
  defaultFilename?: string
}

/**
 * ExportModal Component
 *
 * @param props - The props object for ExportModal
 * @returns The rendered ExportModal component
 */
const ExportModal: React.FC<ExportModalProps> = ({
  title,
  isVisible,
  cancel,
  confirm,
  ExportButtonText,
  ExportButtonType,
  message,
  additionalInputs = [],
  noFilename = false,
  defaultFilename = "",
}) => {
  // State for filename and additional inputs
  const [filename, setFilename] = useState<string>(defaultFilename)
  const [additionalValues, setAdditionalValues] = useState<
    Record<string, unknown>
  >(
    additionalInputs.reduce(
      (acc, input) => ({
        ...acc,
        [input.name]:
          input.type === UI_INPUT_TYPES.CHECKBOX
            ? false
            : input.type === UI_INPUT_TYPES.SELECT
            ? input.defaultValue || (input.multiple ? [] : undefined)
            : "",
      }),
      {} as Record<string, unknown>
    )
  )

  // Update filename when defaultFilename changes
  useEffect(() => {
    setFilename(defaultFilename)
  }, [defaultFilename])

  const handleFilenameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setFilename(e.target.value)
  }

  const handleAdditionalInputChange = (name: string, value: unknown): void => {
    setAdditionalValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const renderInput = (input: AdditionalInput) => {
    const isDisabled =
      typeof input.disabled === "function"
        ? input.disabled(additionalValues)
        : input.disabled

    switch (input.type) {
      case UI_INPUT_TYPES.CHECKBOX:
        return (
          <Checkbox
            checked={additionalValues[input.name] as boolean}
            onChange={(e) =>
              handleAdditionalInputChange(input.name, e.target.checked)
            }
            disabled={isDisabled}
          >
            {input.checkboxLabel}
          </Checkbox>
        )

      case UI_INPUT_TYPES.SELECT:
        return (
          <Select
            mode={input.multiple ? "multiple" : undefined}
            style={{ width: "100%" }}
            placeholder={input.placeholder}
            value={
              additionalValues[input.name] as string | string[] | undefined
            }
            onChange={(value: string | string[]) =>
              handleAdditionalInputChange(input.name, value)
            }
            options={input.options}
            defaultValue={
              typeof input.defaultValue === "string" ||
              Array.isArray(input.defaultValue)
                ? (input.defaultValue as string | string[])
                : undefined
            }
            disabled={isDisabled}
          />
        )

      default:
        return (
          <Input
            value={additionalValues[input.name] as string}
            onChange={(e) =>
              handleAdditionalInputChange(input.name, e.target.value)
            }
            placeholder={input.placeholder}
            type={input.type || UI_INPUT_TYPES.TEXT}
            disabled={isDisabled}
          />
        )
    }
  }

  const handleConfirm = async (): Promise<void> => {
    if (!noFilename && !filename.trim()) {
      notify.error("Error Exporting", "Please enter a filename")
      return
    }

    // Check if any required additional inputs are empty
    const emptyRequired = additionalInputs
      .filter((input) => input.required)
      .some((input) => {
        const value = additionalValues[input.name]

        if (input.type === UI_INPUT_TYPES.SELECT && input.required) {
          return Array.isArray(value) ? value.length === 0 : !value
        }
        if (input.type === UI_INPUT_TYPES.CHECKBOX && input.required) {
          return !value
        }
        return !value
      })

    if (emptyRequired) {
      notify.error("Error Exporting", "Please fill in all required fields")
      return
    }

    try {
      await confirm(filename, additionalValues)
    } catch (error) {
      console.error("Error during export:", error)
      notify.error("Error Exporting", "An error occurred during export")
    }
  }

  return (
    <Modal
      centered
      title={title}
      open={isVisible}
      closable={false}
      footer={null}
    >
      <div className="flex flex-col mb-2">
        <p className="text-md">{message}</p>

        {/* Default filename input */}
        {!noFilename && (
          <div className="mb-2">
            <Input
              value={filename}
              onChange={handleFilenameChange}
              placeholder="Enter filename"
            />
          </div>
        )}

        {/* Additional inputs if any */}
        {additionalInputs.map((input) => (
          <div key={input.name} className="mb-2">
            {input.type !== UI_INPUT_TYPES.CHECKBOX && (
              <label className="block text-sm mb-1">
                {input.label}
                {input.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            {renderInput(input)}
          </div>
        ))}
      </div>

      <div className="flex flex-row space-x-2">
        <DataQButton
          className="ml-auto"
          type="secondary"
          onClick={() => cancel(false)}
        >
          Cancel
        </DataQButton>
        <DataQButton
          type={ExportButtonType || "primary"}
          onClick={handleConfirm}
          isDisabled={!noFilename && !filename.trim()}
        >
          {ExportButtonText || "Export"}
        </DataQButton>
      </div>
    </Modal>
  )
}

export default ExportModal
